import React, { useEffect, useState } from 'react';
import API from '../services/api';
import { useNavigate } from 'react-router-dom';
import './MockTest.css';

// ── Constants ────────────────────────────────────────────────────────────────
const CATEGORY_META = {
  quantitative: { color: '#0f3460', icon: '🔢', label: 'Quantitative' },
  logical: { color: '#8e44ad', icon: '🧠', label: 'Logical' },
  verbal: { color: '#16a085', icon: '📝', label: 'Verbal' },
  programming: { color: '#e94560', icon: '💻', label: 'Programming' },
  sql: { color: '#f39c12', icon: '🗄️', label: 'SQL' },
};

const DIFFICULTY_OPTIONS = [
  { value: 'mixed', label: 'Mixed', icon: '🎲', desc: 'All levels', color: '#0f3460' },
  { value: 'easy', label: 'Easy', icon: '🟢', desc: 'Basic level', color: '#16a085' },
  { value: 'medium', label: 'Medium', icon: '🟡', desc: 'Intermediate', color: '#f39c12' },
  { value: 'hard', label: 'Hard', icon: '🔴', desc: 'Advanced', color: '#e94560' },
];

const QUESTION_COUNTS = [5, 10, 15, 20, 30, 50];

const FLOW_MODES = [
  {
    id: 'quick',
    icon: '⚡',
    title: 'Quick Practice',
    desc: 'Pick topics and jump straight in',
    color: '#0f3460',
    bg: '#eef2ff',
  },
  {
    id: 'category',
    icon: '📂',
    title: 'By Category',
    desc: 'Practice one full category at a time',
    color: '#8e44ad',
    bg: '#f3effe',
  },
  {
    id: 'difficulty',
    icon: '🎯',
    title: 'By Difficulty',
    desc: 'Challenge yourself by difficulty level',
    color: '#e94560',
    bg: '#fff0f3',
  },
  {
    id: 'custom',
    icon: '⚙️',
    title: 'Custom Build',
    desc: 'Full control — pick topics, difficulty & count',
    color: '#16a085',
    bg: '#e6f9f0',
  },
  {
    id: 'personalized',
    icon: '⭐',
    title: 'Personalized Test',
    desc: 'Auto-generated from your mistakes, weak topics, and performance history.',
    color: '#f39c12',
    bg: '#fff8e6',
  },
];

const PERSONALIZED_COUNTS = [20, 30, 40, 50];

// ── Topic chip component ─────────────────────────────────────────────────────
const TopicChip = ({ topic, selected, catColor, onToggle }) => (
  <div
    className={`topic-chip ${selected ? 'selected' : ''}`}
    style={selected ? {
      background: catColor + '18',
      borderColor: catColor,
      color: catColor,
    } : {}}
    onClick={() => onToggle(topic.id)}
  >
    <span>{topic.name}</span>
    <span className="chip-count">{topic.question_count || 0}Q</span>
  </div>
);

// ── Main Component ───────────────────────────────────────────────────────────
const MockTestSetup = () => {
  const navigate = useNavigate();

  const [topics, setTopics] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  // ── Flow selection ─────────────────────────────────────────────────────────
  const [activeFlow, setActiveFlow] = useState(null); // null | 'quick' | 'category' | 'difficulty' | 'custom'

  // ── Shared config ──────────────────────────────────────────────────────────
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [difficulty, setDifficulty] = useState('mixed');
  const [questionCount, setQuestionCount] = useState(10);
  const [selectedCategory, setSelectedCategory] = useState(null);  // for 'category' flow
  const [selectedDiffOnly, setSelectedDiffOnly] = useState('easy'); // for 'difficulty' flow

  // ── Personalized flow ──────────────────────────────────────────────────────
  const [personalizedCount, setPersonalizedCount] = useState(20);
  const [personalizedStatus, setPersonalizedStatus] = useState(null); // { eligible, active_mistakes, weak_topics, max_questions }

  useEffect(() => {
    API.get('/mock/topics')
      .then(res => setTopics(res.data))
      .catch(err => {
        console.error('Topics load error:', err);
        setError('Failed to load practice topics');
      })
      .finally(() => setLoadingTopics(false));

    API.get('/mock/history')
      .then(res => setHistory(res.data))
      .catch(err => console.error('History load error:', err));

    API.get('/mock/personalized/status')
      .then(res => setPersonalizedStatus(res.data))
      .catch(err => console.error('Personalized status load error:', err));
  }, []);

  const groupedTopics = topics.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  const toggleTopic = (id) => {
    setSelectedTopics(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
    setError('');
  };

  const selectAllInCategory = (cat) => {
    const ids = (groupedTopics[cat] || []).map(t => t.id);
    setSelectedTopics(p => [...new Set([...p, ...ids])]);
  };

  const clearAllInCategory = (cat) => {
    const ids = (groupedTopics[cat] || []).map(t => t.id);
    setSelectedTopics(p => p.filter(id => !ids.includes(id)));
  };

  // ── Start mock test ────────────────────────────────────────────────────────
  const handleStart = async (overrides = {}) => {
    const topicIds = overrides.topic_ids || selectedTopics;
    const diff = overrides.difficulty || difficulty;
    const count = overrides.count || questionCount;

    if (topicIds.length === 0) { setError('Please select at least one topic'); return; }

    setStarting(true);
    setError('');
    try {
      const res = await API.post('/mock/start', {
        topic_ids: topicIds,
        difficulty: diff,
        question_count: count,
        session_type: activeFlow,
      });
      navigate('/student/mock/test', {
        state: {
          session_id: res.data.session_id,
          questions: res.data.questions,
          total: res.data.total,
          flowMode: activeFlow,
        },
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start. Try again.');
    } finally {
      setStarting(false);
    }
  };

  // ── Start personalized test ────────────────────────────────────────────────
  const handleStartPersonalized = async () => {
    setStarting(true);
    setError('');
    try {
      const res = await API.post('/mock/personalized/start', {
        question_count: personalizedCount,
      });
      navigate('/student/mock/test', {
        state: {
          session_id: res.data.session_id,
          questions: res.data.questions,
          total: res.data.total,
          flowMode: 'personalized',
        },
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start. Try again.');
    } finally {
      setStarting(false);
    }
  };

  // ── Flow: Quick — select topics freely ────────────────────────────────────
  const renderQuickFlow = () => (
    <div className="flow-panel">
      <div className="flow-panel-title">
        <span>⚡ Quick Practice</span>
        <span className="flow-panel-sub">Select any topics you want to practice</span>
      </div>

      {/* Topic groups */}
      {Object.entries(groupedTopics).map(([cat, tlist]) => {
        const meta = CATEGORY_META[cat] || { color: '#555', icon: '📌', label: cat };
        const allSel = tlist.every(t => selectedTopics.includes(t.id));
        return (
          <div key={cat} className="topic-group">
            <div className="topic-group-header">
              <div className="topic-group-label" style={{ color: meta.color }}>
                {meta.icon} {meta.label.toUpperCase()}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {!allSel
                  ? <button className="mock-text-btn" onClick={() => selectAllInCategory(cat)}>All</button>
                  : <button className="mock-text-btn red" onClick={() => clearAllInCategory(cat)}>Clear</button>
                }
              </div>
            </div>
            <div className="topic-chips">
              {tlist.map(t => (
                <TopicChip key={t.id} topic={t}
                  selected={selectedTopics.includes(t.id)}
                  catColor={meta.color}
                  onToggle={toggleTopic} />
              ))}
            </div>
          </div>
        );
      })}

      {/* Count pills */}
      <div className="flow-row-label">Number of Questions</div>
      <div className="count-pills">
        {QUESTION_COUNTS.map(c => (
          <button key={c}
            className={`count-pill ${questionCount === c ? 'selected' : ''}`}
            onClick={() => setQuestionCount(c)}>
            {c}
          </button>
        ))}
      </div>

      {error && <div className="mock-error">{error}</div>}

      <button
        className="btn-start-mock"
        onClick={() => handleStart()}
        disabled={starting || selectedTopics.length === 0}
      >
        {starting ? 'Preparing…' : `⚡ Start with ${selectedTopics.length} topic${selectedTopics.length !== 1 ? 's' : ''} — ${questionCount} Qs`}
      </button>
    </div>
  );

  // ── Flow: Category — one-click per category ────────────────────────────────
  const renderCategoryFlow = () => (
    <div className="flow-panel">
      <div className="flow-panel-title">
        <span>📂 Practice by Category</span>
        <span className="flow-panel-sub">Pick a full category and start instantly</span>
      </div>

      <div className="category-cards">
        {Object.entries(groupedTopics).map(([cat, tlist]) => {
          const meta = CATEGORY_META[cat] || { color: '#555', icon: '📌', label: cat };
          const totalQ = tlist.reduce((s, t) => s + (parseInt(t.question_count) || 0), 0);
          const isActive = selectedCategory === cat;
          return (
            <div
              key={cat}
              className={`category-card ${isActive ? 'active' : ''}`}
              style={isActive ? { borderColor: meta.color, background: meta.color + '0d' } : {}}
              onClick={() => setSelectedCategory(isActive ? null : cat)}
            >
              <div className="cat-card-icon" style={{ background: meta.color + '18', color: meta.color }}>
                {meta.icon}
              </div>
              <div className="cat-card-info">
                <div className="cat-card-name">{meta.label}</div>
                <div className="cat-card-meta">{tlist.length} topics · {totalQ} questions</div>
              </div>
              {isActive && <div className="cat-card-check" style={{ color: meta.color }}>✓</div>}
            </div>
          );
        })}
      </div>

      {selectedCategory && (
        <>
          <div className="flow-row-label" style={{ marginTop: 16 }}>
            Difficulty for {CATEGORY_META[selectedCategory]?.label || selectedCategory}
          </div>
          <div className="diff-row">
            {DIFFICULTY_OPTIONS.map(d => (
              <button key={d.value}
                className={`diff-pill ${difficulty === d.value ? 'selected' : ''}`}
                style={difficulty === d.value ? { background: d.color, color: 'white', borderColor: d.color } : {}}
                onClick={() => setDifficulty(d.value)}>
                {d.icon} {d.label}
              </button>
            ))}
          </div>

          <div className="flow-row-label">Number of Questions</div>
          <div className="count-pills">
            {QUESTION_COUNTS.map(c => (
              <button key={c}
                className={`count-pill ${questionCount === c ? 'selected' : ''}`}
                onClick={() => setQuestionCount(c)}>
                {c}
              </button>
            ))}
          </div>
        </>
      )}

      {error && <div className="mock-error">{error}</div>}

      <button
        className="btn-start-mock"
        disabled={!selectedCategory || starting}
        onClick={() => {
          const ids = (groupedTopics[selectedCategory] || []).map(t => t.id);
          handleStart({ topic_ids: ids, difficulty, count: questionCount });
        }}
      >
        {starting ? 'Preparing…'
          : selectedCategory
            ? `📂 Start ${CATEGORY_META[selectedCategory]?.label} — ${questionCount} Qs`
            : 'Select a category above'}
      </button>
    </div>
  );

  // ── Flow: Difficulty — pick difficulty first, then topics ─────────────────
  const renderDifficultyFlow = () => (
    <div className="flow-panel">
      <div className="flow-panel-title">
        <span>🎯 Practice by Difficulty</span>
        <span className="flow-panel-sub">Choose your challenge level first</span>
      </div>

      <div className="diff-big-cards">
        {DIFFICULTY_OPTIONS.filter(d => d.value !== 'mixed').map(d => (
          <div
            key={d.value}
            className={`diff-big-card ${selectedDiffOnly === d.value ? 'active' : ''}`}
            style={selectedDiffOnly === d.value ? { borderColor: d.color, background: d.color + '10' } : {}}
            onClick={() => setSelectedDiffOnly(d.value)}
          >
            <div className="diff-big-icon">{d.icon}</div>
            <div className="diff-big-label" style={{ color: selectedDiffOnly === d.value ? d.color : '#333' }}>
              {d.label}
            </div>
            <div className="diff-big-desc">{d.desc}</div>
          </div>
        ))}
      </div>

      <div className="flow-row-label" style={{ marginTop: 20 }}>Topics (optional — leave all selected for full practice)</div>
      <div className="topic-chips" style={{ marginBottom: 16 }}>
        {topics.map(t => {
          const cat = t.category;
          const meta = CATEGORY_META[cat] || { color: '#555' };
          return (
            <TopicChip key={t.id} topic={t}
              selected={selectedTopics.includes(t.id)}
              catColor={meta.color}
              onToggle={toggleTopic} />
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className="mock-text-btn" onClick={() => setSelectedTopics(topics.map(t => t.id))}>Select All Topics</button>
        <button className="mock-text-btn red" onClick={() => setSelectedTopics([])}>Clear All</button>
      </div>

      <div className="flow-row-label">Number of Questions</div>
      <div className="count-pills" style={{ marginBottom: 20 }}>
        {QUESTION_COUNTS.map(c => (
          <button key={c}
            className={`count-pill ${questionCount === c ? 'selected' : ''}`}
            onClick={() => setQuestionCount(c)}>
            {c}
          </button>
        ))}
      </div>

      {error && <div className="mock-error">{error}</div>}

      <button
        className="btn-start-mock"
        disabled={starting}
        onClick={() => {
          const ids = selectedTopics.length > 0 ? selectedTopics : topics.map(t => t.id);
          handleStart({ topic_ids: ids, difficulty: selectedDiffOnly, count: questionCount });
        }}
      >
        {starting ? 'Preparing…' : `🎯 Start ${selectedDiffOnly.charAt(0).toUpperCase() + selectedDiffOnly.slice(1)} — ${questionCount} Qs`}
      </button>
    </div>
  );

  // ── Flow: Custom — full control ────────────────────────────────────────────
  const renderCustomFlow = () => (
    <div className="flow-panel">
      <div className="flow-panel-title">
        <span>⚙️ Custom Build</span>
        <span className="flow-panel-sub">Full control over every parameter</span>
      </div>

      {/* Topics with category groups */}
      {Object.entries(groupedTopics).map(([cat, tlist]) => {
        const meta = CATEGORY_META[cat] || { color: '#555', icon: '📌', label: cat };
        const selCnt = tlist.filter(t => selectedTopics.includes(t.id)).length;
        return (
          <div key={cat} className="topic-group">
            <div className="topic-group-header">
              <div className="topic-group-label" style={{ color: meta.color }}>
                {meta.icon} {meta.label}
                {selCnt > 0 && (
                  <span style={{
                    marginLeft: 8, fontSize: 11, fontWeight: 600,
                    background: meta.color + '18', color: meta.color,
                    padding: '1px 7px', borderRadius: 20,
                  }}>{selCnt} selected</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="mock-text-btn" onClick={() => selectAllInCategory(cat)}>All</button>
                <button className="mock-text-btn red" onClick={() => clearAllInCategory(cat)}>Clear</button>
              </div>
            </div>
            <div className="topic-chips">
              {tlist.map(t => (
                <TopicChip key={t.id} topic={t}
                  selected={selectedTopics.includes(t.id)}
                  catColor={meta.color}
                  onToggle={toggleTopic} />
              ))}
            </div>
          </div>
        );
      })}

      {/* Difficulty */}
      <div className="flow-row-label">Difficulty Level</div>
      <div className="diff-row" style={{ marginBottom: 16 }}>
        {DIFFICULTY_OPTIONS.map(d => (
          <button key={d.value}
            className={`diff-pill ${difficulty === d.value ? 'selected' : ''}`}
            style={difficulty === d.value ? { background: d.color, color: 'white', borderColor: d.color } : {}}
            onClick={() => setDifficulty(d.value)}>
            {d.icon} {d.label}
          </button>
        ))}
      </div>

      {/* Count */}
      <div className="flow-row-label">Number of Questions</div>
      <div className="count-pills">
        {QUESTION_COUNTS.map(c => (
          <button key={c}
            className={`count-pill ${questionCount === c ? 'selected' : ''}`}
            onClick={() => setQuestionCount(c)}>
            {c}
          </button>
        ))}
      </div>

      {/* Summary bar */}
      <div className="custom-summary">
        <div className="custom-summary-item">
          <span className="csi-val">{selectedTopics.length}</span>
          <span className="csi-label">topics</span>
        </div>
        <div className="custom-summary-sep">·</div>
        <div className="custom-summary-item">
          <span className="csi-val">{questionCount}</span>
          <span className="csi-label">questions</span>
        </div>
        <div className="custom-summary-sep">·</div>
        <div className="custom-summary-item">
          <span className="csi-val" style={{ textTransform: 'capitalize' }}>{difficulty}</span>
          <span className="csi-label">difficulty</span>
        </div>
        <div className="custom-summary-sep">·</div>
        <div className="custom-summary-item">
          <span className="csi-val">~{Math.ceil(questionCount * 1.5)}m</span>
          <span className="csi-label">est. time</span>
        </div>
      </div>

      {error && <div className="mock-error">{error}</div>}

      <button
        className="btn-start-mock"
        onClick={() => handleStart()}
        disabled={starting || selectedTopics.length === 0}
      >
        {starting ? 'Preparing…' : '⚙️ Start Custom Test →'}
      </button>
    </div>
  );

  // ── Flow: Personalized — auto-built from weak history ──────────────────────
  const renderPersonalizedFlow = () => {
    const eligible = personalizedStatus?.eligible;
    const counts = PERSONALIZED_COUNTS.filter(
      c => c <= (personalizedStatus?.max_questions || 50)
    );

    return (
      <div className="flow-panel">
        <div className="flow-panel-title">
          <span>⭐ Personalized Test</span>
          <span className="flow-panel-sub">Focused revision on your weakest areas</span>
        </div>

        {!eligible ? (
          <div className="mock-lock">
            🔒 Complete at least one aptitude test to unlock your Personalized Test.
          </div>
        ) : (
          <>
            <div className="personalized-based-on">
              <span className="pbo-label">Built for you, based on:</span>
              <span className="pbo-chip">⚠️ {personalizedStatus?.active_mistakes || 0} Active Mistakes</span>
              <span className="pbo-chip">📉 {personalizedStatus?.weak_topics || 0} Weak Topics</span>
            </div>

            <div className="flow-row-label">Number of Questions</div>
            <div className="count-pills">
              {counts.map(c => (
                <button key={c}
                  className={`count-pill ${personalizedCount === c ? 'selected' : ''}`}
                  onClick={() => setPersonalizedCount(c)}>
                  {c}
                </button>
              ))}
            </div>

            {error && <div className="mock-error">{error}</div>}

            <button
              className="btn-start-mock"
              onClick={handleStartPersonalized}
              disabled={starting}
            >
              {starting ? 'Preparing…' : `⭐ Start Personalized Test — ${personalizedCount} Qs`}
            </button>
          </>
        )}
      </div>
    );
  };

  const renderActiveFlow = () => {
    switch (activeFlow) {
      case 'quick': return renderQuickFlow();
      case 'category': return renderCategoryFlow();
      case 'difficulty': return renderDifficultyFlow();
      case 'custom': return renderCustomFlow();
      case 'personalized': return renderPersonalizedFlow();
      default: return null;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="mock-test-page-content">

        <div className="s-page-header">
          <h2>Mock Test</h2>
          <p>Practice with customized tests — choose your mode to get started</p>
        </div>

        <div className="mock-setup-grid">
          <div className="mock-left">

            {/* ── Mode selector ── */}
            <div className="mock-card">
              <div className="mock-card-title" style={{ marginBottom: 14 }}>
                Choose Practice Mode
              </div>
              <div className="flow-mode-grid">
                {FLOW_MODES.map(m => {
                  const isPersonalized = m.id === 'personalized';
                  const locked = isPersonalized && personalizedStatus && !personalizedStatus.eligible;
                  return (
                    <div
                      key={m.id}
                      className={`flow-mode-card ${activeFlow === m.id ? 'active' : ''} ${locked ? 'disabled' : ''} ${isPersonalized ? 'personalized' : ''}`}
                      style={activeFlow === m.id ? { borderColor: m.color, background: m.bg } : {}}
                      onClick={() => {
                        setActiveFlow(activeFlow === m.id ? null : m.id);
                        setError('');
                        // Auto-select all topics for difficulty flow
                        if (m.id === 'difficulty' && selectedTopics.length === 0) {
                          setSelectedTopics(topics.map(t => t.id));
                        }
                      }}
                    >
                      {isPersonalized && !locked && (
                        <div className="fmc-badge">RECOMMENDED</div>
                      )}
                      <div
                        className="fmc-icon"
                        style={{ background: m.color + '1f', color: m.color }}
                      >
                        {m.icon}
                      </div>
                      <div>
                        <div className="fmc-title" style={{ color: activeFlow === m.id ? m.color : '#1a1a2e' }}>
                          {m.title}
                        </div>
                        <div className="fmc-desc">{m.desc}</div>
                        {isPersonalized && personalizedStatus?.eligible && (
                          <div className="fmc-hint">
                            Based on • {personalizedStatus.active_mistakes || 0} Active Mistakes
                            {' '}• {personalizedStatus.weak_topics || 0} Weak Topics
                          </div>
                        )}
                        {locked && (
                          <div className="fmc-hint">🔒 Finish an aptitude test to unlock</div>
                        )}
                      </div>
                      {activeFlow === m.id && (
                        <div className="fmc-active-dot" style={{ background: m.color }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Active flow panel ── */}
            {activeFlow && (
              <div className="mock-card flow-panel-card">
                {loadingTopics ? (
                  <div className="mock-empty">Loading topics…</div>
                ) : (
                  renderActiveFlow()
                )}
              </div>
            )}

            {/* ── Placeholder when no flow selected ── */}
            {!activeFlow && (
              <div className="mock-card mock-placeholder">
                <div className="mock-placeholder-icon">🎮</div>
                <div className="mock-placeholder-title">Select a practice mode above</div>
                <div className="mock-placeholder-sub">
                  Each mode gives you a different way to target your weak areas
                </div>
              </div>
            )}
          </div>

          {/* ── Right panel: history + tips ── */}
          <div className="mock-right">

            <div className="mock-card">
              <div className="mock-card-title" style={{ marginBottom: 14 }}>
                Recent Sessions
              </div>
              {history.length === 0 ? (
                <div className="mock-empty">
                  <div className="mock-empty-icon">🗂️</div>
                  <div className="mock-empty-title">No sessions yet</div>
                  <div className="mock-empty-sub">Start your first test to see your progress here.</div>
                </div>
              ) : (
                <div className="history-list">
                  {history.slice(0, 8).map((h, i) => {
                    const pct = parseFloat(h.percentage) || 0;
                    const color = pct >= 80 ? '#16a085' : pct >= 60 ? '#f39c12' : '#e94560';
                    const bg = pct >= 80 ? '#e6f9f0' : pct >= 60 ? '#fff8e6' : '#fff0f0';
                    return (
                      <div key={i} className="history-item">
                        <div className="history-info">
                          <div className="history-topic">{h.topic_name || 'Mixed Topics'}</div>
                          <div className="history-meta">
                            {h.total_questions || 0}Q ·{' '}
                            {new Date(h.created_at).toLocaleDateString('en-IN', {
                              day: 'numeric', month: 'short',
                            })}
                          </div>
                        </div>
                        <div className="history-score" style={{ background: bg, color }}>
                          {pct}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="mock-card tips-card">
              <div className="mock-card-title" style={{ marginBottom: 12 }}>💡 Tips</div>
              {[
                'Use Quick mode for a fast warmup',
                'Personalized mode targets your weakest areas',
                'Custom mode for full exam simulation',
                'Aim for 80%+ in every topic',
              ].map((tip, i) => (
                <div key={i} className="tip-item">
                  <span className="tip-num">{i + 1}</span>
                  <span>{tip}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MockTestSetup;